<template>
	<article class="activity-comment-detail">
		<ActivityDetailHeader
			v-if="showHeader"
			:notification-topic="activity.notificationTopic"
			:title="activity.title"
			:timestamp="activity.timestamp"
		/>

		<section class="comment">
			<span class="avatar" aria-hidden="true">{{ actorInitials }}</span>
			<div class="message">
				<strong>{{ actorName }}</strong>
				<p>{{ activity.comment }}</p>
			</div>
		</section>

		<ActivityRelatedTarget
			:target="target"
			:target-id="activity.opportunityIds[0] ?? null"
			:project-id="activity.projectId"
			:loading="loading"
			:load-error="loadError"
		/>

		<ActivityLinkType
			v-if="activity.projectId"
			:link-type="LinkType.PROJECT"
			:title="activity.project || t('activityPage.details.unknownProject')"
			:summary="projectSummary"
			@open="viewProject"
		/>
	</article>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import ActivityDetailHeader from "@/components/activity/ActivityDetailHeader.vue"
import ActivityLinkType from "@/components/activity/ActivityLinkType.vue"
import ActivityRelatedTarget from "@/components/activity/ActivityRelatedTarget.vue"
import { LinkType, type ActivityDetail } from "@/types/activity"
import { getUserInitials } from "@/utils/user"

const props = withDefaults(
	defineProps<{
		activityDetail: ActivityDetail
		loading?: boolean
		loadError?: boolean
		showHeader?: boolean
	}>(),
	{
		loading: false,
		loadError: false,
		showHeader: true,
	}
)

const emit = defineEmits<{
	"view-project": [projectId: string]
}>()

const { t } = useI18n()
const activity = computed(() => {
	const activity = props.activityDetail.activity

	if (activity.linkType !== LinkType.PROJECT) {
		throw new Error("Comment activity must be linked to a project")
	}

	return activity
})
const target = computed(() => props.activityDetail.opportunities[0] ?? null)
const actorName = computed(() => activity.value.actorName ?? t("activityPage.details.unknownAuthor"))
const actorInitials = computed(() => getUserInitials(actorName.value))

const projectSummary = computed(() => {
	if (activity.value.targetsCount === null) {
		return t("nav.hunt")
	}

	return t("activityPage.details.projectSummary", {
		hunt: t("nav.hunt"),
		count: activity.value.targetsCount,
	})
})

function viewProject(): void {
	if (!activity.value.projectId) {
		return
	}

	emit("view-project", activity.value.projectId)
}
</script>

<style scoped>
.activity-comment-detail {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
	width: min(100%, 560px);
	font-family: var(--font-family-primary);
}

.comment {
	display: grid;
	grid-template-columns: var(--size-button-lg) minmax(0, 1fr);
	align-items: start;
	gap: var(--spacing-sm);

	.avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: var(--size-button-lg);
		height: var(--size-button-lg);
		border-radius: var(--border-radius-full);
		background: var(--neutral-100);
		color: var(--neutral-700);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-semibold);
	}

	.message {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		min-width: 0;

		strong {
			color: var(--neutral-900);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-semibold);
		}

		p {
			margin: 0;
			padding: var(--spacing-md);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-lg);
			color: var(--neutral-900);
			font-size: var(--font-size-regular);
			line-height: var(--line-height-relaxed);
			white-space: pre-wrap;
		}
	}
}
</style>
